/**
 * Linked flows section (inside `CapabilityDrawer`) — the workflows currently
 * covering this capability, each removable, plus a search-to-add combobox
 * sourced from the org's flows. Every add/remove PUTs the full desired set to
 * `PUT /product-map/capabilities/:id/workflows` (server does the diff), then
 * invalidates the capability detail + product-map queries so
 * `activeLinkCount`/coverage figures refresh everywhere.
 *
 * Names/health for already-linked flows come from the capability detail's
 * `activeWorkflows`, joined server-side, rather than by looking each id up in
 * the workflows list. That lookup is what used to render every linked flow as
 * a raw `wf_…` id: `GET /workflows` returns raw UUIDs while the capability
 * detail returns prefixed ids, so the map lookup missed every single time and
 * fell through to `name: id`. The two id spaces are reconciled here with
 * `extractUuid`, which accepts either form, so "already linked" stays decidable
 * for the dropdown filter. It cannot return null on these inputs — one side is
 * a DB uuid, the other `encodePrefixedId` output — and asserting that is
 * deliberate: a silent fallback to the unparsed string would quietly restore
 * the very mismatch this file exists to eliminate. The flow name link itself
 * routes on `flow.workflowId` directly (already `wf_`-prefixed) rather than
 * through this UUID reconciliation — `/flows/$workflowId` wants the prefixed
 * form, same as `SeedDataCatalog`'s "View Flow" link.
 *
 * Uses `MultiSelectComboBox` directly rather than `TagPicker`: the trigger is
 * a plain search box (`hideSelectedTags`) and linked flows render below as a
 * list with full, wrapping titles. `renderItem` shows the full flow name in
 * the dropdown too, rather than the truncated `TagBadge` other pickers use
 * (`TagBadge`'s `max-w-[150px]` is depended on elsewhere, so it isn't touched).
 *
 * @module features/product-map/components/LinkedFlowsSection
 */

import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowSquareOut, X } from '@phosphor-icons/react';
import {
  Badge,
  Flex,
  ListCard,
  LoadingOrbit,
  MultiSelectComboBox,
  Text,
  Tooltip,
  TooltipTrigger,
  type MultiSelectOption,
} from '@chatsdet/ui';
import type { WorkflowHealth } from '@chatsdet/types';
import { extractUuid } from '@chatsdet/ids';
import { useWorkflowsQuery } from '../../../hooks/useWorkflows';
import { WORKFLOW_HEALTH_META } from '../../../ui/pages/flow-list/components/cells';
import { useSetCapabilityWorkflows } from '../useProductMap';
import type { ActiveWorkflowLink } from '../types';

interface FlowOption extends MultiSelectOption {
  health: WorkflowHealth;
}

/**
 * Both id spaces reduced to the bare UUID. Non-null by API contract — see the
 * module docblock for why this asserts rather than falling back.
 */
const workflowUuid = (id: string): string => extractUuid(id)!;

function HealthDot({ health }: { health: WorkflowHealth }) {
  const meta = WORKFLOW_HEALTH_META[health];
  return (
    <TooltipTrigger delay={150}>
      <span className="inline-flex shrink-0" aria-label={`Health: ${meta.label}`}>
        <meta.Icon size={13} weight="fill" style={{ color: meta.color }} />
      </span>
      <Tooltip variant="light">{meta.label}</Tooltip>
    </TooltipTrigger>
  );
}

type Props = {
  capabilityId: string;
  activeWorkflows: ActiveWorkflowLink[];
};

export function LinkedFlowsSection({ capabilityId, activeWorkflows }: Props) {
  const setWorkflows = useSetCapabilityWorkflows(capabilityId);
  // Unfiltered so the search-to-add dropdown can find any flow, even a
  // setup/seed one — only the combobox's own typeahead narrows the list.
  const workflowsQuery = useWorkflowsQuery({ pageSize: 200, sortBy: 'name', sortOrder: 'asc' });
  const allWorkflows = workflowsQuery.data.items;

  const options: FlowOption[] = useMemo(
    () =>
      allWorkflows.map((w) => ({
        id: workflowUuid(w.id),
        label: w.name,
        health: w.health,
      })),
    [allWorkflows]
  );

  const selected: FlowOption[] = useMemo(
    () =>
      activeWorkflows.map((w) => ({
        id: workflowUuid(w.workflowId),
        label: w.name,
        health: w.health,
      })),
    [activeWorkflows]
  );

  const sortedLinked = useMemo(
    () => [...activeWorkflows].sort((a, b) => a.name.localeCompare(b.name)),
    [activeWorkflows]
  );

  const handleChange = (items: FlowOption[]) => {
    setWorkflows.mutate(items.map((item) => item.id));
  };

  const handleRemove = (removedUuid: string) => {
    setWorkflows.mutate(
      activeWorkflows.map((w) => workflowUuid(w.workflowId)).filter((id) => id !== removedUuid)
    );
  };

  return (
    <ListCard title="Linked flows" titleAccessory={`${activeWorkflows.length} linked`}>
      <div className="px-3 py-2">
        {workflowsQuery.isLoading ? (
          <LoadingOrbit size="sm" />
        ) : (
          <MultiSelectComboBox<FlowOption>
            aria-label="Search flows to link"
            placeholder="Search flows to link…"
            items={options}
            value={selected}
            onChange={handleChange}
            isDisabled={setWorkflows.isPending}
            // No inline chips inside the trigger — linked flows render as the
            // list below instead, with room for full (non-truncated) titles.
            // The trigger stays a plain search box, placeholder and all.
            hideSelectedTags
            renderItem={(item) => (
              <Flex align="center" gap="sm" className="min-w-0">
                <HealthDot health={item.health} />
                {/* Wraps rather than truncates. The popover is trigger-width so
                    there is room, and a flow name cut off mid-word is precisely
                    what this section is being changed to stop doing. */}
                <span className="min-w-0 text-pretty line-clamp-2">{item.label}</span>
              </Flex>
            )}
          />
        )}
      </div>

      {sortedLinked.length === 0 ? (
        <Text size="sm" className="px-4 py-3 text-muted-foreground">
          No flows cover this capability yet — search above to link one.
        </Text>
      ) : (
        sortedLinked.map((flow) => (
          <div key={flow.workflowId} className="group flex items-center gap-2 px-4 py-2.5 hover:bg-muted/50">
            <HealthDot health={flow.health} />
            <Link
              to="/flows/$workflowId"
              params={{ workflowId: flow.workflowId }}
              target="_blank"
              rel="noreferrer"
              className="flex min-w-0 flex-1 items-center gap-1.5 text-sm text-foreground hover:text-primary hover:underline"
            >
              <span className="min-w-0 text-pretty line-clamp-2">{flow.name}</span>
              <ArrowSquareOut
                size={13}
                className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
              />
            </Link>
            {flow.rerunDueIssueIds.length > 0 && (
              <Badge size="sm" variant="warning">
                Rerun due
              </Badge>
            )}
            <button
              type="button"
              onClick={() => handleRemove(workflowUuid(flow.workflowId))}
              disabled={setWorkflows.isPending}
              aria-label={`Unlink ${flow.name}`}
              className="shrink-0 rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              <X size={14} />
            </button>
          </div>
        ))
      )}
    </ListCard>
  );
}
